import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  class: "flex flex-col gap-y-4 text-left",
  "data-testid": "send-record-step-1"
}
const _hoisted_2 = {
  class: "rounded-lg border border-gray-200 bg-gray-50 px-3 py-2",
  "data-testid": "send-record-context-card"
}
const _hoisted_3 = { class: "flex items-start justify-between gap-2" }
const _hoisted_4 = { class: "font-bold text-textPrimary" }
const _hoisted_5 = { class: "text-xs text-neutral50 mt-0.5" }
const _hoisted_6 = {
  key: 0,
  class: "text-xs text-neutral50 mt-1"
}
const _hoisted_7 = { class: "border-0 p-0 m-0" }
const _hoisted_8 = /*#__PURE__*/_createElementVNode("legend", { class: "sr-only" }, "O que você quer enviar?", -1)
const _hoisted_9 = { class: "grid gap-3 md:grid-cols-2" }
const _hoisted_10 = /*#__PURE__*/_createElementVNode("p", { class: "font-bold text-textPrimary" }, "Registro de Plantão", -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("p", { class: "text-xs text-neutral50" }, " Folha assinada na unidade ou registro feito por app externo. ", -1)
const _hoisted_12 = ["data-testid"]
const _hoisted_13 = ["value", "checked", "disabled", "aria-describedby", "data-testid", "onChange"]
const _hoisted_14 = {
  key: 0,
  class: "block text-xs text-neutral50"
}
const _hoisted_15 = {
  key: 1,
  class: "text-xs text-neutral50"
}
const _hoisted_16 = /*#__PURE__*/_createElementVNode("p", { class: "font-bold text-textPrimary" }, "Registro de Atividade", -1)
const _hoisted_17 = /*#__PURE__*/_createElementVNode("p", { class: "text-xs text-neutral50" }, " Declaração de presença com anexos, quando não houve check no app. ", -1)
const _hoisted_18 = ["checked"]
const _hoisted_19 = /*#__PURE__*/_createElementVNode("span", null, [
  /*#__PURE__*/_createTextVNode(" Jornada "),
  /*#__PURE__*/_createElementVNode("span", { class: "block text-xs text-neutral50" }, "único subtipo desta fase")
], -1)
const _hoisted_20 = /*#__PURE__*/_createElementVNode("span", null, [
  /*#__PURE__*/_createTextVNode(" Produtividade "),
  /*#__PURE__*/_createElementVNode("span", { class: "block text-xs text-neutralLight" }, "em breve")
], -1)
const _hoisted_21 = /*#__PURE__*/_createElementVNode("p", { class: "text-xs text-neutral50" }, " Gera um registro por plantão e entra na mesma fila de auditoria. ", -1)

import { computed } from 'vue'
import {
  CoveredLegs,
  COVERED_LEGS_LABELS,
  EffectiveRegistrationStatus,
  EFFECTIVE_REGISTRATION_STATUS_LABELS,
  DSAttendanceMonitoringType
} from '@/constants/ds-attendance.constants'
import {
  SEND_RECORD_SHEET_OPTIONS,
  isoToTimeInput,
  type ActivitySubtype,
  type SendRecordCategory,
  type SendRecordStationOption
} from './types'

/**
 * Passo 1 de M2 — a bifurcação. "Registro de Plantão" NÃO tem fluxo próprio
 * novo: escolher uma das três subopções fecha este modal e abre o wizard de
 * folha que já existe, pré-preenchido pela vaga. Zero backend novo.
 */

const CATEGORY_RADIO_GROUP = 'send-record-category'

/** `aria-describedby` das opções de folha desabilitadas — o motivo é um texto só. */
const SHEET_BLOCKED_HINT_ID = 'send-record-category-sheet-blocked-hint'


export default /*#__PURE__*/_defineComponent({
  __name: 'StepCategory',
  props: {
    station: {},
    category: {},
    sheetMonitoringType: {},
    activitySubtype: {},
    sheetBlockedReason: {}
  },
  emits: ["update:category", "update:sheetMonitoringType", "update:activitySubtype"],
  setup(__props: any, { emit }) {

const props = __props;





const sheetOptions = SEND_RECORD_SHEET_OPTIONS

/** `name` compartilhado pelas cinco opções: é ele que torna a escolha única. */
function isSheetSelected(monitoringType: DSAttendanceMonitoringType): boolean {
  if (props.sheetBlockedReason) return false
  return props.category === 'FOLHA' && props.sheetMonitoringType === monitoringType
}

const isActivitySelected = computed(
  () => props.category === 'ATIVIDADE' && props.activitySubtype === 'JORNADA'
)

function chooseSheet(monitoringType: DSAttendanceMonitoringType): void {
  // Guarda de verdade, e não só o `disabled` do input: um clique programático
  // (teste, extensão, autofill) não pode reabrir um caminho que o backend recusa.
  if (props.sheetBlockedReason) return
  emit('update:category', 'FOLHA')
  emit('update:sheetMonitoringType', monitoringType)
}

function chooseActivity(): void {
  emit('update:category', 'ATIVIDADE')
  emit('update:activitySubtype', 'JORNADA')
}

const statusLabel = computed(() =>
  props.station?.registrationStatus
    ? EFFECTIVE_REGISTRATION_STATUS_LABELS[props.station.registrationStatus]
    : EFFECTIVE_REGISTRATION_STATUS_LABELS[EffectiveRegistrationStatus.NONE]
)

const statusChipClass = computed(() => {
  const base = 'text-xs font-bold rounded-full px-2 py-0.5 whitespace-nowrap'
  switch (props.station?.registrationStatus) {
    case EffectiveRegistrationStatus.OK:
      return `${base} bg-green-100 text-green-800`
    case EffectiveRegistrationStatus.PARTIAL:
      return `${base} bg-yellow-100 text-yellow-800`
    case EffectiveRegistrationStatus.PENDING:
      return `${base} bg-blue-100 text-blue-800`
    default:
      return `${base} bg-gray-100 text-gray-700`
  }
})

const contextLine = computed(() => {
  const station = props.station
  if (!station) return ''
  const hours =
    station.expectedStart && station.expectedEnd
      ? `${isoToTimeInput(station.expectedStart)}–${isoToTimeInput(station.expectedEnd)}`
      : ''
  return [
    station.healthUnitName,
    station.scheduleName,
    formatDate(station.referenceDate ?? station.expectedStart),
    hours,
    station.shiftName
  ]
    .filter(Boolean)
    .join(' · ')
})

const missingLegLabel = computed(() => {
  const legs = props.station?.missingLegs
  if (!legs) return ''
  return legs === CoveredLegs.BOTH
    ? 'Faltam Check-in e Check-out.'
    : `Falta o ${COVERED_LEGS_LABELS[legs]}.`
})

function formatDate(value?: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('pt-BR')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.station?.doctorName || 'Plantão selecionado'), 1),
        _createElementVNode("span", {
          class: _normalizeClass(statusChipClass.value),
          "data-testid": "send-record-context-status"
        }, _toDisplayString(statusLabel.value), 3)
      ]),
      _createElementVNode("p", _hoisted_5, _toDisplayString(contextLine.value), 1),
      (missingLegLabel.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(missingLegLabel.value), 1))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("fieldset", _hoisted_7, [
      _hoisted_8,
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("div", {
          class: _normalizeClass(["rounded-lg border p-3 flex flex-col gap-2", _ctx.category === 'FOLHA' ? 'border-primary bg-primary5/30' : 'border-gray-200'])
        }, [
          _hoisted_10,
          _hoisted_11,
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(sheetOptions), (option) => {
            return (_openBlock(), _createElementBlock("label", {
              key: option.value,
              class: _normalizeClass(["flex items-center gap-2 text-left text-sm rounded border px-2 py-1.5", [
              _ctx.sheetBlockedReason
                ? 'border-gray-200 text-neutralLight cursor-not-allowed'
                : 'cursor-pointer',
              !_ctx.sheetBlockedReason && isSheetSelected(option.value)
                ? 'border-primary text-primary30 font-bold'
                : !_ctx.sheetBlockedReason
                  ? 'border-gray-200 text-textPrimary'
                  : ''
            ]]),
              "data-testid": `send-record-category-sheet-${option.value}`
            }, [
              _createElementVNode("input", {
                type: "radio",
                name: CATEGORY_RADIO_GROUP,
                class: _normalizeClass(_ctx.sheetBlockedReason ? 'cursor-not-allowed' : 'accent-primary'),
                value: option.value,
                checked: !_ctx.sheetBlockedReason && isSheetSelected(option.value),
                disabled: Boolean(_ctx.sheetBlockedReason) || undefined,
                "aria-describedby": _ctx.sheetBlockedReason ? SHEET_BLOCKED_HINT_ID : undefined,
                "data-testid": `send-record-category-sheet-${option.value}-input`,
                onChange: ($event: any) => (chooseSheet(option.value))
              }, null, 42, _hoisted_13),
              _createElementVNode("span", null, [
                _createTextVNode(_toDisplayString(option.label) + " ", 1),
                (option.hint)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(option.hint), 1))
                  : _createCommentVNode("", true)
              ])
            ], 10, _hoisted_12))
          }), 128)),
          (_ctx.sheetBlockedReason)
            ? (_openBlock(), _createElementBlock("p", {
                key: 0,
                id: SHEET_BLOCKED_HINT_ID,
                class: "text-xs font-bold text-[#B45F09]",
                "data-testid": "send-record-category-sheet-blocked"
              }, _toDisplayString(_ctx.sheetBlockedReason), 1))
            : (_openBlock(), _createElementBlock("p", _hoisted_15, " Ao escolher, seguimos para o envio de folha que já existe hoje. "))
        ], 2),
        _createElementVNode("div", {
          class: _normalizeClass(["rounded-lg border p-3 flex flex-col gap-2", _ctx.category === 'ATIVIDADE' ? 'border-primary bg-primary5/30' : 'border-gray-200'])
        }, [
          _hoisted_16,
          _hoisted_17,
          _createElementVNode("label", {
            class: _normalizeClass(["flex items-center gap-2 text-left text-sm rounded border px-2 py-1.5 cursor-pointer", 
              isActivitySelected.value
                ? 'border-primary text-primary30 font-bold'
                : 'border-gray-200 text-textPrimary'
            ]),
            "data-testid": "send-record-category-activity-JORNADA"
          }, [
            _createElementVNode("input", {
              type: "radio",
              name: CATEGORY_RADIO_GROUP,
              class: "accent-primary",
              value: "JORNADA",
              checked: isActivitySelected.value,
              "data-testid": "send-record-category-activity-JORNADA-input",
              onChange: _cache[0] || (_cache[0] = ($event: any) => (chooseActivity()))
            }, null, 40, _hoisted_18),
            _hoisted_19
          ], 2),
          _createElementVNode("label", {
            class: "flex items-center gap-2 text-left text-sm rounded border border-gray-200 px-2 py-1.5 text-neutralLight cursor-not-allowed",
            "data-testid": "send-record-category-activity-PRODUTIVIDADE"
          }, [
            _createElementVNode("input", {
              type: "radio",
              name: CATEGORY_RADIO_GROUP,
              class: "cursor-not-allowed",
              value: "PRODUTIVIDADE",
              disabled: "",
              "data-testid": "send-record-category-activity-PRODUTIVIDADE-input"
            }),
            _hoisted_20
          ]),
          _hoisted_21
        ], 2)
      ])
    ])
  ]))
}
}

})